// src/pages/onboarding/doctor/steps/StepAbout.jsx
import React, { useRef, useState, useMemo } from "react";
import supabase from "@/services/supabaseClient";
import { getSupabaseAnonKey, getSupabaseBaseUrl } from "@/services/supabaseClient";
import OnboardingTopBar from "@/components/onboarding/OnboardingTopBar";
import OnboardingSection from "@/components/onboarding/OnboardingSection";
import Button from "@/components/ui/Button";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import {
  FOLA_ACTION_ROW_CLASS,
  FOLA_HEADER_ROW_CLASS,
  FOLA_ICON_CLASS,
  FOLA_PRIMARY_CTA_CLASS,
  FOLA_SECONDARY_CTA_CLASS,
  FOLA_TITLE_CLASS,
  FOLA_TONAL_DRAFT_CARD_CLASS,
} from "@/components/fola/folaCardStyles.js";

const MIN = 60;
const MAX = 1000;

function normalizeDoctorNamePart(value) {
  return String(value || "")
    .replace(/\s+/g, " ")
    .trim();
}

function buildDoctorDisplayName({ salutation, lastName, fullName } = {}) {
  const title = normalizeDoctorNamePart(salutation);
  const last = normalizeDoctorNamePart(lastName);
  if (last) return `${title ? `${title} ` : ""}${last}`;

  const parts = normalizeDoctorNamePart(fullName).split(" ").filter(Boolean);
  const derivedLast = parts.length > 1 ? parts[parts.length - 1] : "";
  return derivedLast ? `${title ? `${title} ` : ""}${derivedLast}` : "";
}

function toCleanList(value) {
  const raw = Array.isArray(value)
    ? value
    : String(value || "")
        .split(/[,\n|]+/);
  return raw
    .map((item) =>
      String(item || "")
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
    )
    .filter(Boolean);
}

function isFrenchLocale(locale) {
  return String(locale || "").toLowerCase().startsWith("fr");
}

function formatList(items, locale = "en") {
  const clean = toCleanList(items);
  if (!clean.length) return "";
  if (clean.length === 1) return clean[0];
  const conjunction = isFrenchLocale(locale) ? "et" : "and";
  if (clean.length === 2) return `${clean[0]} ${conjunction} ${clean[1]}`;
  return `${clean.slice(0, -1).join(", ")} ${conjunction} ${clean[clean.length - 1]}`;
}

function hasUnsafeExperienceClaim(draft, hasKnownYears) {
  if (hasKnownYears) return false;
  const text = String(draft || "");
  return (
    /\b(?:over|more than|at least|around|approximately)?\s*\d+\s*\+?\s+years?\b/i.test(text) ||
    /\bextensive experience\b/i.test(text) ||
    /\byears? of (?:clinical )?experience\b/i.test(text) ||
    /\[No\.?\]/i.test(text)
  );
}

function sanitizeFolaBiographyDraft(draft, doctorDisplayName, { hasKnownYears = false, locale = "en" } = {}) {
  const neutralSubject = doctorDisplayName || (isFrenchLocale(locale) ? "le clinicien" : "the clinician");
  return String(draft || "")
    .replace(/\bDr\.?\s*\[Name\]/gi, neutralSubject)
    .replace(/\bDr\s+Name\b/gi, neutralSubject)
    .replace(/\[Name\]/gi, doctorDisplayName || neutralSubject)
    .replace(/\[No\.?\]/gi, "")
    .replace(/\bDr\.?\s+Ndlovu\b/g, neutralSubject)
    .replace(hasKnownYears ? /$a/ : /\s*with\s+\[?\s*No\.?\s*\]?\s*years?[^.]*\./gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

const FRENCH_BIO_LABELS = {
  "Allergy/Immunology": "allergologie et immunologie",
  Pediatric: "pédiatrie",
  "Allergic Diseases": "maladies allergiques",
  "Immune System Disorders": "troubles du système immunitaire",
  anaphylaxis: "anaphylaxie",
  immunoglobulin: "immunoglobuline",
  immunotherapy: "immunothérapie",
  immunodeficiencies: "déficits immunitaires",
  English: "anglais",
  French: "français",
  "Allergy testing and management": "tests et prise en charge des allergies",
  "Professional mole assessment": "évaluation professionnelle des grains de beauté",
  "Food Allergy Management": "prise en charge des allergies alimentaires",
  "Asthma and Respiratory Care": "prise en charge de l’asthme et des troubles respiratoires",
};

function localizeBioValue(value, locale) {
  const raw = String(value || "").trim();
  if (!raw || !isFrenchLocale(locale)) return raw;
  return FRENCH_BIO_LABELS[raw] || raw;
}

function localizeBioList(items, locale) {
  return toCleanList(items).map((item) => localizeBioValue(item, locale));
}

function buildLocalDoctorBioDraft(ctx = {}, locale = "en") {
  const french = isFrenchLocale(locale);
  const name = ctx.doctorDisplayName || ctx.lastName || (french ? "Ce clinicien" : "The clinician");
  const specialty = localizeBioValue(ctx.specialty, locale);
  const subspecialties = localizeBioList(ctx.subspecialties || ctx.subspecialty, locale);
  const languages = localizeBioList(ctx.languages, locale);
  const services = localizeBioList(ctx.services || ctx.consultancyTypes, locale);
  const years = String(ctx.yearsExperience || "").trim();

  if (french) {
    const specialtyPhrase = specialty ? ` est spécialisé(e) en ${specialty}` : " est clinicien(ne)";
    const interestsPhrase = subspecialties.length
      ? ` Ses domaines d'intérêt clinique comprennent ${formatList(subspecialties, locale)}.`
      : "";
    const languagePhrase = languages.length
      ? ` Les consultations peuvent être menées en ${formatList(languages, locale)}.`
      : "";
    const servicesPhrase = services.length
      ? ` Les services proposés comprennent ${formatList(services, locale)}.`
      : "";
    const yearsPhrase = years ? ` ${years} ans d'expérience sont indiqués dans ce profil.` : "";

    return `${name}${specialtyPhrase}.${interestsPhrase}${yearsPhrase}${languagePhrase}${servicesPhrase}`
      .replace(/\s+/g, " ")
      .trim();
  }

  const specialtyPhrase = specialty ? ` specialises in ${specialty}` : " is a clinician";
  const interestsPhrase = subspecialties.length
    ? ` Their practice interests include ${formatList(subspecialties, locale)}.`
    : "";
  const languagePhrase = languages.length
    ? ` They support patients in ${formatList(languages, locale)}.`
    : "";
  const servicesPhrase = services.length
    ? ` Their services include ${formatList(services, locale)}.`
    : "";
  const yearsPhrase = years ? ` The profile records ${years} years of experience.` : "";

  return `${name} ${specialtyPhrase}.${interestsPhrase}${yearsPhrase}${languagePhrase}${servicesPhrase}`
    .replace(/\s+/g, " ")
    .trim();
}

export default function StepAbout({ 
  step, 
  total, 
  value = "", 
  onChange, 
  onBack, 
  onContinue,
  folaHint = {
    degree: "",
    specialty: "",
    subspecialty: "",
    institution: "",
    fieldOfStudy: "",
    qualifications: "",
    languages: "",
    yearsExperience: "",
    trainingSummary: "",
    practiceName: "",
    consultancyTypes: "",
    services: "",
    roleTitle: "Clinician",
    salutation: "",
    firstName: "",
    lastName: "",
    fullName: "",
    doctorDisplayName: "",
  }
}) {
  const { refreshProfile } = useAuth(); // ✅ Handshake hook
  const { languageCode, t } = useLanguage();
  const [text, setText] = useState(value); 
  const [folaDraft, setFolaDraft] = useState("");
  const [isFolaLoading, setIsFolaLoading] = useState(false);
  const [saving, setSaving] = useState(false); // ✅ Saving state
  const [err, setErr] = useState("");
  const [folaNotice, setFolaNotice] = useState("");
  const [folaError, setFolaError] = useState("");
  const textareaRef = useRef(null);

  const len = text.length;
  const valid = len >= MIN && len <= MAX;
  const counter = useMemo(
    () => t("settings.edit.about.counter", "{count}/{max} characters", { count: len, max: MAX }),
    [len, t]
  );
  const existingBio = String(text || "").trim();
  const doctorDisplayName = useMemo(
    () =>
      normalizeDoctorNamePart(folaHint?.doctorDisplayName) ||
      buildDoctorDisplayName({
        salutation: folaHint?.salutation,
        lastName: folaHint?.lastName,
        fullName: folaHint?.fullName,
      }),
    [folaHint?.doctorDisplayName, folaHint?.fullName, folaHint?.lastName, folaHint?.salutation]
  );
  const doctorNameFallback = doctorDisplayName ? "" : "the clinician";
  const specialty = String(folaHint?.specialty || "").trim();
  const subSpecialties = useMemo(
    () => toCleanList(folaHint?.subSpecialties || folaHint?.subspecialties || folaHint?.subspecialty),
    [folaHint?.subSpecialties, folaHint?.subspecialties, folaHint?.subspecialty]
  );
  const knownYearsExperience = String(folaHint?.yearsExperience || "").trim();
  const hasExistingBio = existingBio.length > 0;
  const localDraftContext = useMemo(
    () => ({
      firstName: folaHint?.firstName,
      lastName: normalizeDoctorNamePart(folaHint?.lastName),
      doctorDisplayName,
      specialty,
      subspecialties: subSpecialties,
      languages: folaHint?.languages,
      practiceName: folaHint?.practiceName,
      consultancyTypes: folaHint?.consultancyTypes,
      services: folaHint?.services,
      yearsExperience: knownYearsExperience,
    }),
    [
      doctorDisplayName,
      folaHint?.consultancyTypes,
      folaHint?.firstName,
      folaHint?.languages,
      folaHint?.lastName,
      folaHint?.practiceName,
      folaHint?.services,
      knownYearsExperience,
      specialty,
      subSpecialties,
    ]
  );

  async function requestFolaDraft(mode = "generate") {
    try {
      setIsFolaLoading(true);
      setFolaError("");
      setFolaNotice("");
      const payload = {
        context: "biography",
        details: {
          degree: String(folaHint?.degree || "").trim(),
          specialty: String(folaHint?.specialty || "").trim(),
          subspecialty: String(folaHint?.subspecialty || "").trim(),
          institution: String(folaHint?.institution || "").trim(),
          fieldOfStudy: String(folaHint?.fieldOfStudy || "").trim(),
          qualifications: String(folaHint?.qualifications || "").trim(),
          languages: String(folaHint?.languages || "").trim(),
          yearsExperience: knownYearsExperience,
          yearsExperienceKnown: Boolean(knownYearsExperience),
          trainingSummary: String(folaHint?.trainingSummary || "").trim(),
          practiceName: String(folaHint?.practiceName || "").trim(),
          consultancyTypes: toCleanList(folaHint?.consultancyTypes).join(", "),
          services: toCleanList(folaHint?.services).join(", "),
          verificationStatus: String(folaHint?.verificationStatus || "").trim(),
          registrationVerified: folaHint?.registrationVerified === true,
          roleTitle: String(folaHint?.roleTitle || "Clinician").trim(),
          salutation: String(folaHint?.salutation || "").trim(),
          firstName: String(folaHint?.firstName || "").trim(),
          lastName: String(folaHint?.lastName || "").trim(),
          fullName: String(folaHint?.fullName || "").trim(),
          doctorDisplayName,
          doctorNameFallback,
          existingBio,
          mode,
        },
        locale: languageCode,
      };

      const response = await fetch(`${getSupabaseBaseUrl()}/functions/v1/fola`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: getSupabaseAnonKey(),
          Authorization: `Bearer ${getSupabaseAnonKey()}`,
        },
        body: JSON.stringify(payload),
      });

      const raw = await response.text();
      let data = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        data = {};
      }

      if (!response.ok) {
        throw new Error(String(data?.error || raw || `Fola request failed (${response.status})`));
      }

      let nextDraft = sanitizeFolaBiographyDraft(data?.draft, doctorDisplayName, {
        hasKnownYears: Boolean(knownYearsExperience),
        locale: languageCode,
      });
      if (!nextDraft || hasUnsafeExperienceClaim(nextDraft, Boolean(knownYearsExperience))) {
        nextDraft = buildLocalDoctorBioDraft(localDraftContext, languageCode);
      }

      setFolaDraft(nextDraft);
      setFolaNotice(
        mode === "improve"
          ? t("settings.edit.about.folaImprovedNotice", "Fola prepared an improved draft. Review it before applying.")
          : t("settings.edit.about.folaGeneratedNotice", "Draft generated from your added details.")
      );
    } catch (e) {
      console.error("[Fola Scribe] Error:", e);
      const fallbackDraft = buildLocalDoctorBioDraft(localDraftContext, languageCode);
      setFolaDraft(fallbackDraft);
      setFolaNotice(t("settings.edit.about.folaOfflineNotice", "Fola is offline, so AfriMed™ prepared a safe starter draft from your onboarding details. Review it before applying."));
      setFolaError("");
    } finally {
      setIsFolaLoading(false);
    }
  }

  function applyFolaDraft() {
    const nextDraft = String(folaDraft || "").trim();
    if (!nextDraft) return;
    setText(nextDraft);
    onChange?.(nextDraft);
    setFolaNotice(t("settings.edit.about.folaAppliedNotice", "Fola's draft has been applied. Review and edit before saving."));
  }

  function editFolaDraft() {
    const nextDraft = String(folaDraft || "").trim();
    if (!nextDraft) return;
    setText(nextDraft);
    onChange?.(nextDraft);
    setFolaDraft("");
    setFolaNotice(t("settings.edit.about.folaEditNotice", "Draft moved into About you. Edit it before saving."));
    window.requestAnimationFrame(() => {
      textareaRef.current?.focus();
    });
  }

  // ✅ THE SYNC: Save to 'about_you' column
  const handleContinue = async () => {
    const bioText = text.trim();
    if (bioText.length < MIN || saving) return;

    try {
      setSaving(true);
      setErr("");

      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (user?.id) {
        const { error } = await supabase
          .from("doctor_profiles")
          .update({
            about_you: bioText,
            updated_at: new Date().toISOString(),
          })
          .eq("user_id", user.id);

        if (error) throw error;

        if (typeof refreshProfile === "function") {
          try {
            await refreshProfile();
          } catch (refreshError) {
            console.warn("[StepAbout] refreshProfile skipped:", refreshError);
          }
        }
      } else {
        console.warn("[StepAbout] no active auth user, continuing with local onboarding state only");
      }

      onContinue?.();
    } catch (e) {
      console.warn("[StepAbout] save skipped:", e);
      setErr("");
      onContinue?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-dvh flex flex-col">
      <OnboardingTopBar step={step} total={total} onBack={onBack} />
      <OnboardingSection
        header={
          <div className="space-y-2">
            <h2 className="text-title-large text-on-surface">
              {t("settings.edit.about.title", isFrenchLocale(languageCode) ? "À propos de vous" : "About you")}
            </h2>
            <p className="text-body-medium text-on-surface-var">
              {t("settings.edit.about.subtitle", isFrenchLocale(languageCode) ? "Présentez brièvement votre parcours, votre expérience et votre approche clinique." : "Tell your patients a little about yourself and what you do.")}
            </p>
          </div>
        }
        footer={
          <Button
            size="lg"
            disabled={!valid || saving}
            onClick={handleContinue} // Points to the sync function we built
          >
            {saving ? t("common.saving", isFrenchLocale(languageCode) ? "Enregistrement..." : "Saving...") : t("common.continue", isFrenchLocale(languageCode) ? "Continuer" : "Continue")}
          </Button>
        }
        footerShellClassName="onboarding-mobile-solid-footer"
      >
        <label className="block">
          <div className="rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] p-4 shadow-[0_0_0_1px_rgba(0,0,0,0.04)]">
            <div className="text-body-medium text-on-surface-var mb-2">
              {t("settings.edit.about.label", isFrenchLocale(languageCode) ? "À propos de vous" : "About you")}
            </div>
            <textarea
              ref={textareaRef}
              className="w-full resize-none bg-transparent outline-none border-0 p-0
                         text-body-large text-on-surface leading-[1.4] h-40 placeholder:text-on-surface-var"
              placeholder={t("settings.edit.about.placeholder", isFrenchLocale(languageCode) ? "Clinicien engagé dans une prise en charge centrée sur le patient..." : "Dedicated clinician specialising in patient-centred care...")}
              value={text}
              maxLength={MAX}
              onChange={(e) => {
                const next = e.target.value;
                setText(next);
                onChange?.(next);
              }}
              spellCheck
            />
          </div>

          <div className="mt-2 flex items-center justify-between">
            <span className="text-body-large text-on-surface-var">{counter}</span>
            <span className={`text-sm ${len < MIN ? "text-[rgb(var(--afrimed-primary))]" : "text-[rgb(var(--afrimed-on-muted))]"}`}>
              {t("settings.edit.about.minimum", "Minimum {min} characters required", { min: MIN })}
            </span>
          </div>
        </label>

        <div className="mt-6 space-y-4">
          <div className={FOLA_HEADER_ROW_CLASS}>
            <img src="/assets/images/fola.svg" alt="Fola" className={FOLA_ICON_CLASS} />
            <div className={FOLA_TITLE_CLASS}>
              {t("settings.edit.about.folaHelpTitle", isFrenchLocale(languageCode) ? "Besoin d’aide pour rédiger votre présentation professionnelle ?" : "Need help writing your professional bio?")}
            </div>
          </div>
          <div>
              <p className="mt-2 text-body-medium text-on-surface-var">
                {t("settings.edit.about.folaHelpCopy", isFrenchLocale(languageCode) ? "Ajoutez quelques lignes sur votre spécialité, vos années d’expérience et votre approche, puis laissez Fola générer un brouillon modifiable. Relisez et ajustez le texte avant de l’enregistrer." : "Add a couple of lines about your specialty, years of experience and approach, and let Fola generate an editable draft. Review and edit before saving.")}
              </p>
              <p className="mt-2 text-body-small text-on-surface-var">
                {t("settings.edit.about.folaSettingsHint", isFrenchLocale(languageCode) ? "Vous pourrez mettre à jour votre biographie professionnelle plus tard dans les Réglages." : "You can update your professional bio later in Settings.")}
              </p>
              {folaNotice ? (
                <div className="mt-3 rounded-[10px] border border-[rgba(29,79,215,0.12)] bg-[rgb(var(--clr-surface-cont-highest))] px-3 py-2 text-body-small text-on-surface">
                  {folaNotice}
                </div>
              ) : null}
              {folaError ? (
                <div className="mt-3 rounded-[10px] border border-[rgba(248,83,58,0.16)] bg-[rgba(248,83,58,0.06)] px-3 py-2 text-body-small text-[rgb(var(--schemes-critical))]">
                  {folaError}
                </div>
              ) : null}
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  variant="tonal"
                  className={FOLA_PRIMARY_CTA_CLASS}
                  disabled={isFolaLoading}
                  onClick={() => requestFolaDraft("generate")}
                >
                  {isFolaLoading
                    ? t("settings.edit.about.folaPreparing", isFrenchLocale(languageCode) ? "Fola prépare un brouillon..." : "Fola is preparing a draft...")
                    : t("settings.edit.about.folaGenerate", isFrenchLocale(languageCode) ? "Générer une présentation avec Fola" : "Generate bio with Fola")}
                </Button>
                {hasExistingBio ? (
                  <Button
                    variant="ghost"
                    className={FOLA_SECONDARY_CTA_CLASS}
                    disabled={isFolaLoading}
                    onClick={() => requestFolaDraft("improve")}
                  >
                    {t("settings.edit.about.folaImprove", isFrenchLocale(languageCode) ? "Améliorer la présentation actuelle avec Fola" : "Improve current bio with Fola")}
                  </Button>
                ) : null}
              </div>
          </div>

          {(folaDraft || isFolaLoading) ? (
            <div>
              <div className={FOLA_HEADER_ROW_CLASS}>
                <img src="/assets/images/fola.svg" alt="Fola" className={FOLA_ICON_CLASS} />
                <div className={FOLA_TITLE_CLASS}>
                  {t("settings.edit.about.folaTitle", isFrenchLocale(languageCode) ? "Puis-je vous aider à rédiger votre présentation professionnelle aujourd’hui ?" : "Can I help you write a professional biography today?")}
                </div>
              </div>

              <div className={FOLA_TONAL_DRAFT_CARD_CLASS}>
                {isFolaLoading ? (
                  <div className="flex items-center gap-2 py-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                    <div className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse [animation-delay:0.2s]" />
                    <div className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse [animation-delay:0.4s]" />
                    <span className="ml-2 text-label-medium text-primary/60">
                      {t("settings.edit.about.folaLoading", isFrenchLocale(languageCode) ? "Fola prépare un brouillon..." : "Fola is preparing a draft...")}
                    </span>
                  </div>
                ) : (
                  <p className="text-body-medium italic leading-relaxed text-on-surface">
                    "{folaDraft}"
                  </p>
                )}
              </div>

              {!isFolaLoading && folaDraft ? (
                <div className={FOLA_ACTION_ROW_CLASS}>
                  <Button
                    variant="tonal"
                    className={FOLA_PRIMARY_CTA_CLASS}
                    onClick={applyFolaDraft}
                  >
                    {t("settings.edit.about.folaApply", isFrenchLocale(languageCode) ? "Appliquer le brouillon de Fola" : "Apply Fola's Draft")}
                  </Button>
                  <Button
                    variant="ghost"
                    className={FOLA_SECONDARY_CTA_CLASS}
                    onClick={editFolaDraft}
                  >
                    {t("settings.edit.about.folaEdit", isFrenchLocale(languageCode) ? "Modifier le brouillon" : "Edit draft")}
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </OnboardingSection>
    </div>
  );
}
